import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import OtpVerification from './OtpVerification';
import LoginPage from './LoginPage';
import { ROUTES } from '../../routes/routes';
import { useTheme } from '../../contexts/ThemeContext';
import { FiUsers, FiLayers, FiBell, FiMail, FiShield, FiSun, FiMoon, FiAward, FiMessageSquare } from 'react-icons/fi';
import Logo from '../../components/ui/Logo';
import { strings } from '../../strings/strings';

const WelcomePage = () => {
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [userEmail, setUserEmail]         = useState('');
  const navigate                          = useNavigate();
  const { isDark, toggleTheme }           = useTheme();

  if (localStorage.getItem('isAuthenticated') === 'true') {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }

  const handleLoginSuccess = (email) => { setUserEmail(email); setShowOtpScreen(true); };

  const handleOtpSuccess = (results) => {
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('accessToken',     results?.accessToken);
    localStorage.setItem('refreshToken',    results?.refreshToken);
    localStorage.setItem('superAdmin',      JSON.stringify(results?.superAdmin));
    navigate(ROUTES.ADMIN_DASHBOARD);
  };

  return (
    <div className={`h-screen flex flex-col relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0B0F19]' : 'bg-slate-50'
    }`}>
      
      {/* ─────────────────────────────────────────
          UNIFIED GLOWING BACKGROUND
      ───────────────────────────────────────── */}
      <div className={`absolute top-[-20%] left-[-10%] w-[60%] h-[60%] blur-[130px] rounded-full mix-blend-multiply transition-colors duration-300 ${
        isDark ? 'bg-primary/10 mix-blend-screen' : 'bg-primary/20'
      } pointer-events-none`} />
      <div className={`absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] blur-[120px] rounded-full mix-blend-multiply transition-colors duration-300 ${
        isDark ? 'bg-blue-500/5 mix-blend-screen' : 'bg-blue-300/10'
      } pointer-events-none`} />
      <div className={`absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] ${
        isDark ? 'opacity-[0.02]' : 'opacity-[0.04]'
      } pointer-events-none`} />

      {/* ─────────────────────────────────────────
          LEFT PANEL — Simple & Professional
      ───────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

      <div className="hidden lg:flex w-1/2 max-w-[580px] flex-col relative z-10 bg-transparent">
        <div className="flex flex-col h-full px-12 py-10 xl:px-16 xl:py-14">
          
          {/* Logo Section */}
          <div className="flex items-center">
            <Logo size="md"/>
          </div>

          {/* Hero Typography */}
          <div className="my-6 max-w-md">
            <span className={`inline-block px-3.5 py-1 rounded-full text-xs font-bold tracking-wider uppercase mb-5 ${
              isDark ? 'bg-primary/10 text-primary' : 'bg-primary/10 text-primary-dark'
            }`}>
              {strings.PLATFORM_COMMAND_CENTER}
            </span>
            <h1 className={`text-2xl xl:text-4xl font-extrabold leading-[1.2] tracking-tight mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {strings.ENTERPRISE_HR_OPERATIONS} <br/>
              <span className={`text-primary dark:text-primary-dark bg-clip-text bg-gradient-to-r`}>{strings.UNIFIED}</span>
            </h1>
            <p className={`text-base leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {strings.HERO_DESCRIPTION}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 max-w-lg">
            
            {/* Capability 1: Company Management */}
            <div className={`p-3.5 rounded-2xl border flex items-center gap-3 transition-all duration-300 group hover:translate-y-[-2px] ${
              isDark 
                ? 'bg-white/[0.02] border-white/5 hover:border-primary/20 hover:bg-white/[0.04] hover:shadow-xl hover:shadow-black/20' 
                : 'bg-white/80 border-slate-200/60 shadow-sm hover:border-slate-300 hover:bg-white hover:shadow-md'
            }`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105 ${
                isDark ? 'bg-primary/10 text-primary' : 'bg-primary/10 text-primary-dark'
              }`}>
                <FiLayers className="w-4.5 h-4.5" />
              </div>
              <span className={`font-bold text-sm tracking-wide transition-colors duration-200 group-hover:text-primary-dark dark:group-hover:text-primary ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                {strings.COMPANEE_MANAGEMENT}
              </span>
            </div>

            {/* Capability 2: Announcement Management */}
            <div className={`p-3.5 rounded-2xl border flex items-center gap-3 transition-all duration-300 group hover:translate-y-[-2px] ${
              isDark 
                ? 'bg-white/[0.02] border-white/5 hover:border-primary/20 hover:bg-white/[0.04] hover:shadow-xl hover:shadow-black/20' 
                : 'bg-white/80 border-slate-200/60 shadow-sm hover:border-slate-300 hover:bg-white hover:shadow-md'
            }`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105 ${
                isDark ? 'bg-primary/10 text-primary' : 'bg-primary/10 text-primary-dark'
              }`}>
                <FiBell className="w-4.5 h-4.5" />
              </div>
              <span className={`font-bold text-sm tracking-wide transition-colors duration-200 group-hover:text-primary-dark dark:group-hover:text-primary ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                {strings.ANNOUNCEMENT_MANAGEMENT}
              </span>
            </div>

            {/* Capability 3: Contact Requests */}
            <div className={`p-3.5 rounded-2xl border flex items-center gap-3 transition-all duration-300 group hover:translate-y-[-2px] ${
              isDark 
                ? 'bg-white/[0.02] border-white/5 hover:border-primary/20 hover:bg-white/[0.04] hover:shadow-xl hover:shadow-black/20' 
                : 'bg-white/80 border-slate-200/60 shadow-sm hover:border-slate-300 hover:bg-white hover:shadow-md'
            }`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105 ${
                isDark ? 'bg-primary/10 text-primary' : 'bg-primary/10 text-primary-dark'
              }`}>
                <FiMail className="w-4.5 h-4.5" />
              </div>
              <span className={`font-bold text-sm tracking-wide transition-colors duration-200 group-hover:text-primary-dark dark:group-hover:text-primary ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                {strings.CONTACT_REQUEST_MANAGEMENT}
              </span>
            </div>

            {/* Capability 4: Brands Management */}
            <div className={`p-3.5 rounded-2xl border flex items-center gap-3 transition-all duration-300 group hover:translate-y-[-2px] ${
              isDark 
                ? 'bg-white/[0.02] border-white/5 hover:border-primary/20 hover:bg-white/[0.04] hover:shadow-xl hover:shadow-black/20' 
                : 'bg-white/80 border-slate-200/60 shadow-sm hover:border-slate-300 hover:bg-white hover:shadow-md'
            }`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105 ${
                isDark ? 'bg-primary/10 text-primary' : 'bg-primary/10 text-primary-dark'
              }`}>
                <FiAward className="w-4.5 h-4.5" />
              </div>
              <span className={`font-bold text-sm tracking-wide transition-colors duration-200 group-hover:text-primary-dark dark:group-hover:text-primary ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                {strings.BRANDS_MANAGEMENT}
              </span>
            </div>

            {/* Capability 5: Reviews Management */}
            <div className={`p-3.5 rounded-2xl border flex items-center gap-3 transition-all duration-300 group hover:translate-y-[-2px] ${
              isDark 
                ? 'bg-white/[0.02] border-white/5 hover:border-primary/20 hover:bg-white/[0.04] hover:shadow-xl hover:shadow-black/20' 
                : 'bg-white/80 border-slate-200/60 shadow-sm hover:border-slate-300 hover:bg-white hover:shadow-md'
            }`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105 ${
                isDark ? 'bg-primary/10 text-primary' : 'bg-primary/10 text-primary-dark'
              }`}>
                <FiMessageSquare className="w-4.5 h-4.5" />
              </div>
              <span className={`font-bold text-sm tracking-wide transition-colors duration-200 group-hover:text-primary-dark dark:group-hover:text-primary ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                {strings.REVIEWS_MANAGEMENT}
              </span>
            </div>

            {/* Capability 6: Dashboard */}
            <div className={`p-3.5 rounded-2xl border flex items-center gap-3 transition-all duration-300 group hover:translate-y-[-2px] ${
              isDark 
                ? 'bg-white/[0.02] border-white/5 hover:border-primary/20 hover:bg-white/[0.04] hover:shadow-xl hover:shadow-black/20' 
                : 'bg-white/80 border-slate-200/60 shadow-sm hover:border-slate-300 hover:bg-white hover:shadow-md'
            }`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105 ${
                isDark ? 'bg-primary/10 text-primary' : 'bg-primary/10 text-primary-dark'
              }`}>
                <FiShield className="w-4.5 h-4.5" />
              </div>
              <span className={`font-bold text-sm tracking-wide transition-colors duration-200 group-hover:text-primary-dark dark:group-hover:text-primary ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                {strings.DASHBOARD}
              </span>
            </div>

          </div>
          {/* end capabilities grid */}
        </div>
      </div>
      <div className="flex-1 flex flex-col relative z-10 bg-transparent h-full overflow-y-auto">
        
        {/* Top Header (Theme Toggle + Mobile Logo) */}
        <header className="absolute top-0 w-full flex items-center justify-between px-6 py-6 z-20">
          <div className="lg:hidden flex items-center gap-2.5">
            <div className="p-1.5 bg-primary/10 rounded-lg">
              <Logo size="lg" />
            </div>

          </div>
          <div className="hidden lg:block" />

          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className={`relative flex items-center w-16 h-8 rounded-full p-1 transition-all duration-300 focus:outline-none ${
              isDark 
                ? 'bg-slate-900/80 border border-slate-800/80 hover:border-primary/30 shadow-inner shadow-black/40' 
                : 'bg-slate-100 border border-slate-200/80 hover:border-slate-300 shadow-inner shadow-slate-250/20'
            }`}
          >
            {/* Sliding Background */}
            <div 
              className={`absolute top-0.5 bottom-0.5 left-0.5 w-[26px] h-[26px] rounded-full transition-transform duration-300 flex items-center justify-center ${
                isDark 
                  ? 'translate-x-8 bg-primary/20 border border-primary/40' 
                  : 'translate-x-0 bg-white border border-slate-200 shadow-sm'
              }`}
            />
            
            {/* Icons */}
            <div className="w-full flex items-center justify-between px-1.5 relative z-10 pointer-events-none">
              <FiSun 
                className={`w-3.5 h-3.5 transition-colors duration-300 ${
                  isDark ? 'text-slate-500' : 'text-primary-dark font-bold scale-110'
                }`} 
              />
              <FiMoon 
                className={`w-3.5 h-3.5 transition-colors duration-300 ${
                  isDark ? 'text-primary scale-110 font-bold' : 'text-slate-400'
                }`} 
              />
            </div>
          </button>
        </header>

        {/* Center Content Area */}
        <main className="flex-1 flex items-center justify-center px-4 sm:px-12 py-12 lg:py-0">
          <div className="w-full max-w-[440px]">
            <div className={`p-8 sm:p-10 rounded-[2rem] transition-all duration-300 border ${
              isDark 
                ? 'bg-[#111827]/80 border-slate-800/80 shadow-2xl shadow-black/50 backdrop-blur-sm' 
                : 'bg-white border-slate-200/60 shadow-[0_8px_30px_rgb(0,0,0,0.04)]'
            }`}>
              {!showOtpScreen && (
                <div className="mb-8 text-center animate-fadeIn">
                  <div className={`mx-auto w-12 h-12 rounded-2xl flex items-center justify-center mb-5 ${
                    isDark ? 'bg-primary/10 text-primary' : 'bg-primary/10 text-primary'
                  }`}>
                    <FiUsers className="w-6 h-6" />
                  </div>
                  <h2 className={`text-2xl sm:text-3xl font-extrabold tracking-tight mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {strings.SUPER_ADMIN_PORTAL}
                  </h2>
                  <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {strings.ACCESS_CONTROL_CENTER}
                  </p>
                </div>
              )}

              <div className={`w-full ${!showOtpScreen ? 'mt-6' : ''}`}>
                {!showOtpScreen
                  ? <LoginPage onLoginSuccess={handleLoginSuccess} />
                  : <OtpVerification email={userEmail} onSuccess={handleOtpSuccess} />
                }
              </div>
            </div>
          </div>
        </main>

      
      </div>

      </div>
      <footer className="relative z-10 w-full py-4 text-center">
        <p className={`text-xs font-medium ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
          © {new Date().getFullYear()}{' '}
         
         {strings.OBJECT_INFOTECH}   . {strings.ALL_RIGHTS_RESERVED}
        </p>
      </footer>
      
    </div>
  );
};

export default WelcomePage;
